import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Filter, X } from 'lucide-react';
import { useLanguage } from '@/hooks/use-language';

interface FilterState {
  groups: string[];
  adminOnly: boolean;
  hasEmail: boolean;
  hasPhone: boolean;
}

interface AdvancedMemberFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  availableGroups: string[];
}

export function AdvancedMemberFilters({ filters, onFiltersChange, availableGroups }: AdvancedMemberFiltersProps) {
  const { t } = useLanguage();

  const handleGroupToggle = (group: string) => {
    const newGroups = filters.groups.includes(group)
      ? filters.groups.filter(g => g !== group)
      : [...filters.groups, group];
    onFiltersChange({ ...filters, groups: newGroups });
  };

  const clearFilters = () => {
    onFiltersChange({
      groups: [],
      adminOnly: false,
      hasEmail: false,
      hasPhone: false,
    });
  };

  const activeFilterCount = 
    filters.groups.length + 
    (filters.adminOnly ? 1 : 0) + 
    (filters.hasEmail ? 1 : 0) + 
    (filters.hasPhone ? 1 : 0);

  return (
    <div className="flex items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            {t('members.filters')}
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-2 rounded-full h-5 w-5 p-0 flex items-center justify-center">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm">{t('members.advanced_filters')}</h4>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  {t('common.clear')}
                </Button>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs font-medium text-muted-foreground mb-2 block">
                  {t('members.member_type')}
                </Label>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="admin-filter"
                    checked={filters.adminOnly}
                    onCheckedChange={(checked) => 
                      onFiltersChange({ ...filters, adminOnly: checked as boolean })
                    }
                  />
                  <label htmlFor="admin-filter" className="text-sm cursor-pointer">
                    {t('members.admins_only')}
                  </label>
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-muted-foreground mb-2 block">
                  {t('members.contact_info')}
                </Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="email-filter"
                      checked={filters.hasEmail}
                      onCheckedChange={(checked) => 
                        onFiltersChange({ ...filters, hasEmail: checked as boolean })
                      }
                    />
                    <label htmlFor="email-filter" className="text-sm cursor-pointer">
                      {t('members.has_email')}
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="phone-filter"
                      checked={filters.hasPhone}
                      onCheckedChange={(checked) => 
                        onFiltersChange({ ...filters, hasPhone: checked as boolean })
                      }
                    />
                    <label htmlFor="phone-filter" className="text-sm cursor-pointer">
                      {t('members.has_phone')}
                    </label>
                  </div>
                </div>
              </div>

              {availableGroups.length > 0 && (
                <div>
                  <Label className="text-xs font-medium text-muted-foreground mb-2 block">
                    {t('groups.filter.select')} ({filters.groups.length})
                  </Label>
                  <div className="max-h-48 overflow-y-auto space-y-2 border rounded-md p-2">
                    {availableGroups.map(group => (
                      <div key={group} className="flex items-center space-x-2">
                        <Checkbox
                          id={`group-${group}`}
                          checked={filters.groups.includes(group)}
                          onCheckedChange={() => handleGroupToggle(group)}
                        />
                        <label htmlFor={`group-${group}`} className="text-sm cursor-pointer flex-1">
                          {group}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-1">
          {filters.adminOnly && (
            <Badge variant="secondary" className="text-xs">
              {t('members.admins_only')}
            </Badge>
          )}
          {filters.groups.slice(0, 2).map(group => (
            <Badge key={group} variant="secondary" className="text-xs">
              {group}
            </Badge>
          ))}
          {filters.groups.length > 2 && (
            <Badge variant="secondary" className="text-xs">
              +{filters.groups.length - 2}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
